import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Card,
  CardContent,
  Divider,
  Grid,
  Switch,
} from '@mui/material';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import Typography from '@mui/material/Typography';
import { RichTreeView } from '@mui/x-tree-view';
import { jsonToTree } from '../utils/json-to-tree';
import InfoTooltipIcon from '@/components/icons/InfoTooltipIcon';
import BasicEnergyManageCard from '@/features/service-management/components/basic-energy/ManageBasicEnergySubscription';

const ServiceManagementDrawer = ({
  open,
  onClose,
  account,
  handleAddService,
  handleRemoveService,
  portfolioAnalysisManageComponent,
}) => {
  const servicesList = [
    {
      Id: 1,
      Name: 'Basis',
    },
    {
      Id: 2,
      Name: 'PVO',
    },
    {
      Id: 3,
      Name: 'EnergyXplorer',
    },
    {
      Id: 4,
      Name: 'JyskEnergiESG',
    },
    {
      Id: 5,
      Name: 'PortfolioAnalysis',
    },
    {
      Id: 6,
      Name: 'DatahubIntegration',
    },
    {
      Id: 7,
      Name: 'DashboardPowerBasic',
    },
    {
      Id: 8,
      Name: 'DashboardHeatBasic',
    },
    {
      Id: 9,
      Name: 'DashboardWaterBasic',
    },
  ];

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: '40%', height: '100%' },
      }}
    >
      <Box p={2}>
        <Typography variant="h6" gutterBottom>
          Account Details
        </Typography>
        {account ? (
          <>
            <Grid container spacing={2}>
              <Grid item lg={6}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <HeaderDisplay label={'Account'} />
                    <MultiValueDisplay label={'Name'} value={account.Name} />
                    <MultiValueDisplay label={'Id'} value={account.AccountId} />
                    <MultiValueDisplay
                      label={'Address'}
                      value={account.Address}
                    />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item lg={6}>
                <Card variant="outlined">
                  <CardContent>
                    <HeaderDisplay label={'Services'} />
                    {servicesList.map((service, index) => {
                      const hasService = account.Services?.some(
                        (s) => s.Id === service.Id,
                      );
                      return (
                        <Box key={index}>
                          {index != 0 && <Divider sx={{ mx: 1 }} />}
                          <Box
                            key={service.Id}
                            display="flex"
                            alignItems="center"
                            sx={{ pl: 2, mb: 1 }}
                          >
                            <Typography variant="body2" sx={{ flexGrow: 1 }}>
                              {service.Name}
                            </Typography>
                            <Switch
                              checked={hasService}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  handleAddService(service.Id);
                                } else {
                                  handleRemoveService(service.Id);
                                }
                              }}
                              inputProps={{
                                'aria-label': `toggle ${service.Name}`,
                              }}
                            />
                          </Box>
                        </Box>
                      );
                    })}
                    {portfolioAnalysisManageComponent}
                  </CardContent>
                </Card>
              </Grid>
              <Grid item lg={12}>
                <Card variant="outlined">
                  <CardContent>
                    <BasicEnergyManageCard account={account} />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item lg={12}>
                <Card variant="outlined">
                  <CardContent>
                    <HeaderDisplay label={'Users'} />
                    {account.UserRoles?.map((ur, index) => (
                      <Box key={index} mb={2}>
                        <MultiValueDisplay
                          label={ur.Roles.reduce(
                            (acc, role) => acc + ', ' + role,
                            '',
                          ).slice(2)}
                          value={ur.User.Email}
                        />
                        <MultiValueDisplay
                          label={'Time Zone'}
                          value={ur.User.TimeZone || 'Not set'}
                          valueVariant="caption"
                        />
                        {index < account.UserRoles.length - 1 && (
                          <Divider sx={{ my: 2 }} />
                        )}
                      </Box>
                    ))}
                  </CardContent>
                </Card>
              </Grid>
              <Grid item lg={12}>
                <Card variant="outlined">
                  <CardContent>
                    <HeaderDisplay label={'API outputs'} />
                    {account.jsonObjects.map((o, index) => {
                      return (
                        <Accordion key={index}>
                          <AccordionSummary>
                            <Typography>{o.name}</Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <RichTreeView
                              items={jsonToTree(o.object, o.idKey, o.nameKey)}
                            />
                          </AccordionDetails>
                        </Accordion>
                      );
                    })}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>{' '}
          </>
        ) : (
          <Typography variant="body1">No account data available.</Typography>
        )}
      </Box>
    </Drawer>
  );
};

export default ServiceManagementDrawer;

const HeaderDisplay = ({ label, description }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        mb: 2,
      }}
    >
      <Typography variant="body1" color="textSecondary" fontWeight="bold">
        {label}
      </Typography>
      {description && <InfoTooltipIcon title={description} />}
    </Box>
  );
};

const MultiValueDisplay = ({ label, value, valueVariant = 'h6' }) => {
  return (
    <Box>
      <Typography variant="body2" color="textSecondary">
        {label}
      </Typography>
      <Typography variant={valueVariant} color="textPrimary">
        {value}
      </Typography>
    </Box>
  );
};
